'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Calculator } from 'lucide-react';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import {
  EstimateCard,
  useProjectEstimates,
} from '@/features/design/estimate';
import { SectionHeader, ScrollableCarousel, SectionPanel } from './section-components';

interface ProjectEstimatesSectionProps {
  projectId: string;
}

/**
 * Project-level Estimates carousel on the building page (FE3) — placed right
 * before the Albums section. Mirrors `ProjectAlbumsSection`: a section header
 * with a count + navigation circle to the dedicated Estimates page, and a
 * horizontal carousel of estimate cards. ALWAYS rendered — an empty-state
 * panel when the project has no estimates (Apple/Android parity).
 */
export function ProjectEstimatesSection({
  projectId,
}: ProjectEstimatesSectionProps) {
  const t = useTranslations('common.estimate');

  const { data: estimates = [], isLoading } = useProjectEstimates(projectId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <SectionHeader title={t('page.title')} />
        <SectionPanel>
          <div className="flex h-full items-center gap-2 overflow-hidden p-3">
            <Skeleton className="aspect-[3/4] h-full flex-shrink-0 rounded-xl" />
            <Skeleton className="aspect-[3/4] h-full flex-shrink-0 rounded-xl" />
          </div>
        </SectionPanel>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title={t('page.title')}
        count={estimates.length > 0 ? estimates.length : undefined}
        actions={[{ label: t('page.title'), href: `/project/${projectId}/estimates` }]}
      />
      {estimates.length > 0 ? (
        <ScrollableCarousel>
          {estimates.map((estimate) => (
            <EstimateCard
              key={estimate.id}
              estimate={estimate}
              projectId={projectId}
            />
          ))}
        </ScrollableCarousel>
      ) : (
        <SectionPanel>
          <Link
            href={`/project/${projectId}/estimates`}
            className="flex h-full w-full flex-col items-center justify-center gap-2"
          >
            <Calculator
              className="h-9 w-9 text-muted-foreground/50"
              strokeWidth={1.5}
            />
            <span className="text-card-meta text-muted-foreground/70">
              {t('page.empty.title')}
            </span>
          </Link>
        </SectionPanel>
      )}
    </div>
  );
}
